import React, { useState, useEffect } from 'react';
import { 
    Table, Button, message, Tag, Space, Tooltip, Input, Dropdown, Menu, 
    Modal, Form, Select, Switch, Tabs, Descriptions, Card, Row, Col,
    DatePicker, InputNumber, Popconfirm, Statistic, Badge, Divider, Alert
} from 'antd';
import { 
    EditOutlined, DeleteOutlined, EllipsisOutlined, PlusOutlined, 
    EyeOutlined, CopyOutlined, SwapOutlined, HistoryOutlined,
    ShopOutlined, HomeOutlined, AuditOutlined, CheckCircleOutlined,
    CloseCircleOutlined, ClockCircleOutlined, FileTextOutlined,
    FilterOutlined, ReloadOutlined, DownloadOutlined, PrinterOutlined
} from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import api from "../../components/api/api-base";
import { useBusinessContext } from '../../components/BusinessContext';
import { useAuthContext } from '../../components/AuthProvider';
import moment from 'moment';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const AssetsPage = () => {
    const [activeTab, setActiveTab] = useState('1');
    const [loading, setLoading] = useState(false);
    const [instancesLoading, setInstancesLoading] = useState(false);
    const [movementsLoading, setMovementsLoading] = useState(false);
    const [auditsLoading, setAuditsLoading] = useState(false);
    
    // Data states
    const [fixedAssets, setFixedAssets] = useState([]);
    const [filteredFixedAssets, setFilteredFixedAssets] = useState([]);
    const [assetInstances, setAssetInstances] = useState([]);
    const [filteredInstances, setFilteredInstances] = useState([]);
    const [assetMovements, setAssetMovements] = useState([]);
    const [filteredMovements, setFilteredMovements] = useState([]);
    const [assetAudits, setAssetAudits] = useState([]);
    const [filteredAudits, setFilteredAudits] = useState([]);
    const [auditStatuses, setAuditStatuses] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [shops, setShops] = useState([]);
    
    // Search states
    const [searchText, setSearchText] = useState('');
    const [instanceSearchText, setInstanceSearchText] = useState('');
    const [movementSearchText, setMovementSearchText] = useState('');
    const [auditSearchText, setAuditSearchText] = useState('');
    
    // Filter states
    const [assetFilter, setAssetFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    
    // Selection states
    const [selectedAssetForInstances, setSelectedAssetForInstances] = useState(null);
    const [selectedInstanceForMovements, setSelectedInstanceForMovements] = useState(null);
    const [selectedInstanceForAudits, setSelectedInstanceForAudits] = useState(null);
    
    // Modal states
    const [isAssetModalVisible, setIsAssetModalVisible] = useState(false);
    const [isInstanceModalVisible, setIsInstanceModalVisible] = useState(false);
    const [isMovementModalVisible, setIsMovementModalVisible] = useState(false);
    const [isAuditModalVisible, setIsAuditModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
    
    // Selected items
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [selectedInstance, setSelectedInstance] = useState(null);
    const [selectedMovement, setSelectedMovement] = useState(null);
    const [selectedAudit, setSelectedAudit] = useState(null);
    const [viewingItem, setViewingItem] = useState(null);
    const [viewType, setViewType] = useState('asset');
    
    // Summary stats
    const [summaryStats, setSummaryStats] = useState({
        totalAssets: 0,
        totalInstances: 0,
        totalValue: 0,
        depreciableAssets: 0,
        inServiceCount: 0,
        underMaintenanceCount: 0,
        pendingAudits: 0,
        completedAudits: 0
    });
    
    // Forms
    const [assetForm] = Form.useForm();
    const [instanceForm] = Form.useForm();
    const [movementForm] = Form.useForm();
    const [auditForm] = Form.useForm();
    
    const navigate = useNavigate();
    const { businessId, businessName } = useBusinessContext();
    const { token } = useAuthContext();

    const depreciationMethods = [
        "Straight Line",
        "Double Declining Balance",
        "Sum of Years Digits",
        "Units of Production"
    ];

    const assetStatuses = [
        "In Service",
        "Under Maintenance",
        "Disposed",
        "In Transit",
        "Reserved",
        "Retired"
    ];

    const auditStatusOptions = [
        
        "In Progress",
        "Completed",
        "Verified",
        "Discrepancy Found",
        "Resolved"
    ];

    const movementReasons = [
        "Transfer to different location",
        "Maintenance",
        "Repair",
        "Upgrade",
        "Disposal",
        "Loan",
        "Return from loan",
        "Inventory count adjustment",
        "Damage",
        "Other"
    ];

    const unitsOfMeasure = [
        "Each",
        "Set",
        "Pair",
        "Kit",
        "System"
    ];

    useEffect(() => {
        if (businessId) {
            fetchFixedAssets();
            fetchSuppliers();
            fetchWarehouses();
            fetchShops();
            fetchAuditStatuses();
        }
    }, [businessId]);

    useEffect(() => {
        calculateSummaryStats();
    }, [fixedAssets, assetInstances, assetAudits]);

    const fetchFixedAssets = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/fixedassets/business/${businessId}`);
            console.log("Fetched fixed assets:", response.data);
            setFixedAssets(response.data || []);
            setFilteredFixedAssets(response.data || []);
        } catch (error) {
            console.error("Fetch Fixed Assets Error:", error);
            message.error(`Failed to fetch fixed assets: ${error.message || 'Unknown error'}`);
            setFixedAssets([]);
            setFilteredFixedAssets([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssetInstancesByAssetId = async (fixedAssetId) => {
        setInstancesLoading(true);
        try {
            const response = await api.get(`/fixedassetinstances/fixedasset/${fixedAssetId}`);
            console.log(`Fetched instances for asset ${fixedAssetId}:`, response.data);
            return response.data || [];
        } catch (error) {
            console.error("Fetch Asset Instances Error:", error);
            message.error(`Failed to fetch asset instances: ${error.message || 'Unknown error'}`);
            return [];
        } finally {
            setInstancesLoading(false);
        }
    };

    const fetchAssetMovementsByInstanceId = async (instanceId) => {
        setMovementsLoading(true);
        try {
            const response = await api.get(`/fixedassetmovements/instance/${instanceId}`);
            console.log(`Fetched movements for instance ${instanceId}:`, response.data);
            return response.data || [];
        } catch (error) {
            console.error("Fetch Asset Movements Error:", error);
            message.error(`Failed to fetch asset movements: ${error.message || 'Unknown error'}`);
            return [];
        } finally {
            setMovementsLoading(false);
        }
    };

    const fetchAssetAuditsByInstanceId = async (instanceId) => {
        setAuditsLoading(true);
        try {
            const response = await api.get(`/fixedassetaudits/instance/${instanceId}`);
            console.log(`Fetched audits for instance ${instanceId}:`, response.data);
            return response.data || [];
        } catch (error) {
            console.error("Fetch Asset Audits Error:", error);
            message.error(`Failed to fetch asset audits: ${error.message || 'Unknown error'}`);
            return [];
        } finally {
            setAuditsLoading(false);
        }
    };

    const fetchAuditStatuses = async () => {
        try {
            const response = await api.get('/fixedassetaudits/statuses');
            console.log("Fetched audit statuses:", response.data);
            setAuditStatuses(response.data || auditStatusOptions);
        } catch (error) {
            console.error("Fetch Audit Statuses Error:", error);
            setAuditStatuses(auditStatusOptions);
        }
    };

    const fetchSuppliers = async () => {
        try {
            const response = await api.get(`/suppliers/business/${businessId}`);
            console.log("Fetched suppliers:", response.data);
            setSuppliers(response.data || []);
        } catch (error) {
            console.error("Fetch Suppliers Error:", error);
            message.error(`Failed to fetch suppliers: ${error.message || 'Unknown error'}`);
            setSuppliers([]);
        }
    };

    const fetchWarehouses = async () => {
        try {
            const response = await api.get(`/warehouses/business/${businessId}`);
            console.log("Fetched warehouses:", response.data);
            setWarehouses(response.data || []);
        } catch (error) {
            console.error("Fetch Warehouses Error:", error);
            message.error(`Failed to fetch warehouses: ${error.message || 'Unknown error'}`);
            setWarehouses([]);
        }
    };

    const fetchShops = async () => {
        try {
            const response = await api.get(`/shops/business/${businessId}`);
            console.log("Fetched shops:", response.data);
            setShops(response.data || []);
        } catch (error) {
            console.error("Fetch Shops Error:", error);
            message.error(`Failed to fetch shops: ${error.message || 'Unknown error'}`);
            setShops([]);
        }
    };

    const calculateSummaryStats = () => {
        const totalAssets = fixedAssets.length;
        const totalInstances = assetInstances.length;
        const totalValue = assetInstances.reduce((sum, instance) => sum + (instance.acquisitionCost || 0), 0);
        const depreciableAssets = fixedAssets.filter(a => a.isDepreciable).length;
        const inServiceCount = assetInstances.filter(i => i.status === 'In Service').length;
        const underMaintenanceCount = assetInstances.filter(i => i.status === 'Under Maintenance').length;
        const pendingAudits = assetAudits.filter(a => a.status === 'Pending' || a.status === 'In Progress').length;
        const completedAudits = assetAudits.filter(a => a.status === 'Completed' || a.status === 'Verified').length;

        setSummaryStats({
            totalAssets,
            totalInstances,
            totalValue,
            depreciableAssets,
            inServiceCount,
            underMaintenanceCount,
            pendingAudits,
            completedAudits
        });
    };

    const handleAssetSelectForInstances = async (assetId) => {
        if (!assetId) {
            setAssetInstances([]);
            setFilteredInstances([]);
            setSelectedAssetForInstances(null);
            return;
        }

        const asset = fixedAssets.find(a => a.id === assetId);
        setSelectedAssetForInstances(asset);
        
        const instances = await fetchAssetInstancesByAssetId(assetId);
        setAssetInstances(instances);
        setFilteredInstances(instances);
        
        // Clear selections for movements and audits
        setSelectedInstanceForMovements(null);
        setSelectedInstanceForAudits(null);
        setAssetMovements([]);
        setFilteredMovements([]);
        setAssetAudits([]);
        setFilteredAudits([]);
    };

    const handleInstanceSelectForMovements = async (instanceId) => {
        if (!instanceId) {
            setAssetMovements([]);
            setFilteredMovements([]);
            setSelectedInstanceForMovements(null);
            return;
        }

        const instance = assetInstances.find(i => i.id === instanceId);
        setSelectedInstanceForMovements(instance);
        
        const movements = await fetchAssetMovementsByInstanceId(instanceId);
        setAssetMovements(movements);
        setFilteredMovements(movements);
    };

    const handleInstanceSelectForAudits = async (instanceId) => {
        if (!instanceId) {
            setAssetAudits([]);
            setFilteredAudits([]);
            setSelectedInstanceForAudits(null);
            return;
        }

        const instance = assetInstances.find(i => i.id === instanceId);
        setSelectedInstanceForAudits(instance);
        
        const audits = await fetchAssetAuditsByInstanceId(instanceId);
        setAssetAudits(audits);
        setFilteredAudits(audits);
    };

    const truncateText = (text, maxLength) => {
        if (text && text.length > maxLength) {
            return text.substring(0, maxLength) + '...';
        }
        return text;
    };

    const handleAssetSearch = (value) => {
        setSearchText(value);
        const filtered = fixedAssets.filter(
            (asset) =>
                asset.name?.toLowerCase().includes(value.toLowerCase()) ||
                asset.code?.toLowerCase().includes(value.toLowerCase()) ||
                asset.description?.toLowerCase().includes(value.toLowerCase()) ||
                asset.manufacturer?.toLowerCase().includes(value.toLowerCase()) ||
                asset.model?.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredFixedAssets(filtered);
    };

    const handleInstanceSearch = (value) => {
        setInstanceSearchText(value);
        const filtered = assetInstances.filter(
            (instance) =>
                instance.serialNumber?.toLowerCase().includes(value.toLowerCase()) ||
                instance.tagNumber?.toLowerCase().includes(value.toLowerCase()) ||
                instance.currentLocationDescription?.toLowerCase().includes(value.toLowerCase()) ||
                instance.invoiceNumber?.toLowerCase().includes(value.toLowerCase()) ||
                instance.status?.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredInstances(filtered);
    };

    const handleMovementSearch = (value) => {
        setMovementSearchText(value);
        const filtered = assetMovements.filter(
            (movement) =>
                movement.reason?.toLowerCase().includes(value.toLowerCase()) ||
                movement.notes?.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredMovements(filtered);
    };

    const handleAuditSearch = (value) => {
        setAuditSearchText(value);
        const filtered = assetAudits.filter(
            (audit) =>
                audit.status?.toLowerCase().includes(value.toLowerCase()) ||
                audit.notes?.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredAudits(filtered);
    };

    const applyFilters = () => {
        let filtered = [...assetAudits];
        
        if (statusFilter) {
            filtered = filtered.filter(a => a.status === statusFilter);
        }
        
        if (dateRange && dateRange[0] && dateRange[1]) {
            filtered = filtered.filter(a => 
                moment(a.auditDate).isBetween(dateRange[0], dateRange[1], 'day', '[]')
            );
        }
        
        setFilteredAudits(filtered);
        setIsFilterModalVisible(false);
    };

    const resetFilters = () => {
        setStatusFilter(null);
        setDateRange(null);
        setFilteredAudits(assetAudits);
        setIsFilterModalVisible(false);
    };

    // Fixed Asset CRUD
    const handleAddAsset = () => {
        setSelectedAsset(null);
        assetForm.resetFields();
        assetForm.setFieldsValue({
            businessId: businessId,
            isDepreciable: false,
            estimatedUnitCost: 0,
            usefulLifeYears: 0,
            model: ''
        });
        setIsAssetModalVisible(true);
    };

    const handleEditAsset = (asset) => {
        setSelectedAsset(asset);
        assetForm.setFieldsValue({
            code: asset.code,
            name: asset.name,
            description: asset.description,
            manufacturer: asset.manufacturer,
            model: asset.model,
            estimatedUnitCost: asset.estimatedUnitCost,
            defaultUnitOfMeasure: asset.defaultUnitOfMeasure,
            isDepreciable: asset.isDepreciable,
            usefulLifeYears: asset.usefulLifeYears,
            depreciationMethod: asset.depreciationMethod
        });
        setIsAssetModalVisible(true);
    };

    const handleDeleteAsset = async (id) => {
        try {
            await api.delete(`/fixedassets/${id}`);
            message.success("Fixed asset deleted successfully!");
            fetchFixedAssets();
            
            if (selectedAssetForInstances?.id === id) {
                setSelectedAssetForInstances(null);
                setAssetInstances([]);
                setFilteredInstances([]);
                setSelectedInstanceForMovements(null);
                setSelectedInstanceForAudits(null);
                setAssetMovements([]);
                setFilteredMovements([]);
                setAssetAudits([]);
                setFilteredAudits([]);
            }
        } catch (error) {
            console.error("Delete Asset Error:", error);
            message.error(`Failed to delete fixed asset: ${error.message || 'Unknown error'}`);
        }
    };

    const handleAssetModalOk = async () => {
        try {
            const values = await assetForm.validateFields();
            setLoading(true);
            
            const payload = {
                businessId: businessId,
                code: values.code,
                name: values.name,
                description: values.description,
                manufacturer: values.manufacturer,
                model: values.model,
                estimatedUnitCost: values.estimatedUnitCost ? parseFloat(values.estimatedUnitCost) : 0,
                defaultUnitOfMeasure: values.defaultUnitOfMeasure,
                isDepreciable: values.isDepreciable,
                usefulLifeYears: values.isDepreciable ? values.usefulLifeYears : 0,
                depreciationMethod: values.isDepreciable ? values.depreciationMethod : null
            };

            if (selectedAsset) {
                await api.put(`/fixedassets/${selectedAsset.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Fixed asset updated successfully!");
            } else {
                await api.post(`/fixedassets`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Fixed asset created successfully!");
            }

            setIsAssetModalVisible(false);
            assetForm.resetFields();
            setSelectedAsset(null);
            fetchFixedAssets();
        } catch (error) {
            console.error("Asset Save Error:", error);
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    message.error(`${key}: ${errors[key].join(', ')}`);
                });
            } else {
                message.error(`Failed to save fixed asset: ${error.message || 'Unknown error'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Asset Instance CRUD
    const handleAddInstance = () => {
        if (!selectedAssetForInstances) {
            message.warning("Please select a fixed asset first");
            return;
        }
        
        setSelectedInstance(null);
        instanceForm.resetFields();
        instanceForm.setFieldsValue({
            fixedAssetId: selectedAssetForInstances.id,
            acquisitionDate: moment(),
            status: 'In Service',
            acquisitionCost: 0,
            supplierId: null
        });
        setIsInstanceModalVisible(true);
    };

    const handleEditInstance = (instance) => {
        setSelectedInstance(instance);
        instanceForm.setFieldsValue({
            fixedAssetId: instance.fixedAssetId,
            serialNumber: instance.serialNumber,
            tagNumber: instance.tagNumber,
            currentWarehouseId: instance.currentWarehouseId,
            currentShopId: instance.currentShopId,
            currentLocationDescription: instance.currentLocationDescription,
            status: instance.status,
            acquisitionDate: instance.acquisitionDate ? moment(instance.acquisitionDate) : null,
            acquisitionCost: instance.acquisitionCost,
            supplierId: instance.supplierId,
            invoiceNumber: instance.invoiceNumber,
            purchaseOrderNumber: instance.purchaseOrderNumber
        });
        setIsInstanceModalVisible(true);
    };

    const handleDeleteInstance = async (id) => {
        try {
            await api.delete(`/fixedassetinstances/${id}`);
            message.success("Asset instance deleted successfully!");
            
            if (selectedAssetForInstances) {
                const instances = await fetchAssetInstancesByAssetId(selectedAssetForInstances.id);
                setAssetInstances(instances);
                setFilteredInstances(instances);
            }
            
            if (selectedInstanceForMovements?.id === id) {
                setSelectedInstanceForMovements(null);
                setAssetMovements([]);
                setFilteredMovements([]);
            }
            
            if (selectedInstanceForAudits?.id === id) {
                setSelectedInstanceForAudits(null);
                setAssetAudits([]);
                setFilteredAudits([]);
            }
        } catch (error) {
            console.error("Delete Instance Error:", error);
            message.error(`Failed to delete asset instance: ${error.message || 'Unknown error'}`);
        }
    };

    const handleInstanceModalOk = async () => {
        try {
            const values = await instanceForm.validateFields();
            setLoading(true);
            
            const payload = {
                fixedAssetId: values.fixedAssetId,
                serialNumber: values.serialNumber,
                tagNumber: values.tagNumber,
                currentWarehouseId: values.currentWarehouseId,
                currentShopId: values.currentShopId,
                currentLocationDescription: values.currentLocationDescription,
                status: values.status,
                acquisitionDate: values.acquisitionDate ? values.acquisitionDate.toISOString() : null,
                acquisitionCost: values.acquisitionCost ? parseFloat(values.acquisitionCost) : 0,
                supplierId: values.supplierId || null,
                invoiceNumber: values.invoiceNumber,
                purchaseOrderNumber: values.purchaseOrderNumber
            };

            if (selectedInstance) {
                await api.put(`/fixedassetinstances/${selectedInstance.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Asset instance updated successfully!");
            } else {
                await api.post(`/fixedassetinstances`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Asset instance created successfully!");
            }

            setIsInstanceModalVisible(false);
            instanceForm.resetFields();
            setSelectedInstance(null);
            
            if (selectedAssetForInstances) {
                const instances = await fetchAssetInstancesByAssetId(selectedAssetForInstances.id);
                setAssetInstances(instances);
                setFilteredInstances(instances);
            }
        } catch (error) {
            console.error("Instance Save Error:", error);
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    message.error(`${key}: ${errors[key].join(', ')}`);
                });
            } else {
                message.error(`Failed to save asset instance: ${error.message || 'Unknown error'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Asset Movement CRUD
    const handleAddMovement = () => {
        if (!selectedInstanceForMovements) {
            message.warning("Please select an asset instance first");
            return;
        }
        
        setSelectedMovement(null);
        movementForm.resetFields();
        movementForm.setFieldsValue({
            fixedAssetInstanceId: selectedInstanceForMovements.id,
            movementDate: moment(),
            reason: 'Transfer to different location'
        });
        setIsMovementModalVisible(true);
    };

    const handleEditMovement = (movement) => {
        setSelectedMovement(movement);
        movementForm.setFieldsValue({
            fixedAssetInstanceId: movement.fixedAssetInstanceId,
            movementDate: movement.movementDate ? moment(movement.movementDate) : null,
            fromWarehouseId: movement.fromWarehouseId,
            toWarehouseId: movement.toWarehouseId,
            fromShopId: movement.fromShopId,
            toShopId: movement.toShopId,
            reason: movement.reason,
            notes: movement.notes
        });
        setIsMovementModalVisible(true);
    };

    const handleDeleteMovement = async (id) => {
        try {
            await api.delete(`/fixedassetmovements/${id}`);
            message.success("Asset movement deleted successfully!");
            
            if (selectedInstanceForMovements) {
                const movements = await fetchAssetMovementsByInstanceId(selectedInstanceForMovements.id);
                setAssetMovements(movements);
                setFilteredMovements(movements);
            }
        } catch (error) {
            console.error("Delete Movement Error:", error);
            message.error(`Failed to delete asset movement: ${error.message || 'Unknown error'}`);
        }
    };

    const handleMovementModalOk = async () => {
        try {
            const values = await movementForm.validateFields();
            setLoading(true);
            
            const payload = {
                fixedAssetInstanceId: values.fixedAssetInstanceId,
                movementDate: values.movementDate ? values.movementDate.toISOString() : new Date().toISOString(),
                fromWarehouseId: values.fromWarehouseId || null,
                toWarehouseId: values.toWarehouseId || null,
                fromShopId: values.fromShopId || null,
                toShopId: values.toShopId || null,
                reason: values.reason,
                notes: values.notes
            };

            if (selectedMovement) {
                await api.put(`/fixedassetmovements/${selectedMovement.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Asset movement updated successfully!");
            } else {
                await api.post(`/fixedassetmovements`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Asset movement created successfully!");
            }

            setIsMovementModalVisible(false);
            movementForm.resetFields();
            setSelectedMovement(null);
            
            if (selectedInstanceForMovements) {
                const movements = await fetchAssetMovementsByInstanceId(selectedInstanceForMovements.id);
                setAssetMovements(movements);
                setFilteredMovements(movements);
            }
        } catch (error) {
            console.error("Movement Save Error:", error);
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    message.error(`${key}: ${errors[key].join(', ')}`);
                });
            } else {
                message.error(`Failed to save asset movement: ${error.message || 'Unknown error'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Asset Audit CRUD
    const handleAddAudit = () => {
        if (!selectedInstanceForAudits) {
            message.warning("Please select an asset instance first");
            return;
        }
        
        setSelectedAudit(null);
        auditForm.resetFields();
        auditForm.setFieldsValue({
            fixedAssetInstanceId: selectedInstanceForAudits.id,
            auditDate: moment(),
            status: 'Found'
        });
        setIsAuditModalVisible(true);
    };

    const handleEditAudit = (audit) => {
        setSelectedAudit(audit);
        auditForm.setFieldsValue({
            fixedAssetInstanceId: audit.fixedAssetInstanceId,
            auditDate: audit.auditDate ? moment(audit.auditDate) : null,
            status: audit.status,
            notes: audit.notes
        });
        setIsAuditModalVisible(true);
    };

    const handleDeleteAudit = async (id) => {
        try {
            await api.delete(`/fixedassetaudits/${id}`);
            message.success("Asset audit deleted successfully!");
            
            if (selectedInstanceForAudits) {
                const audits = await fetchAssetAuditsByInstanceId(selectedInstanceForAudits.id);
                setAssetAudits(audits);
                setFilteredAudits(audits);
            }
        } catch (error) {
            console.error("Delete Audit Error:", error);
            message.error(`Failed to delete asset audit: ${error.message || 'Unknown error'}`);
        }
    };

    const handleAuditModalOk = async () => {
        try {
            const values = await auditForm.validateFields();
            setLoading(true);
            
            const payload = {
                fixedAssetInstanceId: values.fixedAssetInstanceId,
                auditDate: values.auditDate ? values.auditDate.toISOString() : new Date().toISOString(),
                status: values.status,
                notes: values.notes
            };

            if (selectedAudit) {
                await api.put(`/fixedassetaudits/${selectedAudit.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Asset audit updated successfully!");
            } else {
                await api.post(`/fixedassetaudits`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Asset audit created successfully!");
            }

            setIsAuditModalVisible(false);
            auditForm.resetFields();
            setSelectedAudit(null);
            
            if (selectedInstanceForAudits) {
                const audits = await fetchAssetAuditsByInstanceId(selectedInstanceForAudits.id);
                setAssetAudits(audits);
                setFilteredAudits(audits);
            }
        } catch (error) {
            console.error("Audit Save Error:", error);
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    message.error(`${key}: ${errors[key].join(', ')}`);
                });
            } else {
                message.error(`Failed to save asset audit: ${error.message || 'Unknown error'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (item, type) => {
        setViewingItem(item);
        setViewType(type);
        setIsViewModalVisible(true);
    };

    const getSupplierName = (supplierId) => {
        const supplier = suppliers.find(s => s.id === supplierId);
        return supplier?.name || supplierId;
    };

    const getWarehouseName = (warehouseId) => {
        const warehouse = warehouses.find(w => w.id === warehouseId);
        return warehouse?.name || warehouseId;
    };

    const getShopName = (shopId) => {
        const shop = shops.find(s => s.id === shopId);
        return shop?.name || shopId;
    };

    const getAuditStatusColor = (status) => {
        const colorMap = {
            'Pending': 'orange',
            'In Progress': 'blue',
            'Completed': 'green',
            'Verified': 'cyan',
            'Discrepancy Found': 'red',
            'Resolved': 'purple'
        };
        return colorMap[status] || 'default';
    };

    const getAuditStatusIcon = (status) => {
        switch(status) {
            case 'Pending': return <ClockCircleOutlined />;
            case 'In Progress': return <HistoryOutlined />;
            case 'Completed': return <CheckCircleOutlined />;
            case 'Verified': return <CheckCircleOutlined />;
            case 'Discrepancy Found': return <CloseCircleOutlined />;
            case 'Resolved': return <CheckCircleOutlined />;
            default: return <FileTextOutlined />;
        }
    };

    const exportToCSV = (data, filename) => {
        // Implementation for CSV export
        message.success(`Exporting ${filename}...`);
    };

    const printReport = () => {
        window.print();
    };

    // Table Columns
    const assetColumns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 100,
            fixed: 'left',
            sorter: (a, b) => a.code.localeCompare(b.code),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Button type="link" onClick={() => handleViewDetails(record, 'asset')} style={{ padding: 0 }}>
                    <strong>{text}</strong>
                </Button>
            ),
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text) => (
                <Tooltip title={text}>
                    {truncateText(text, 25)}
                </Tooltip>
            ),
        },
        {
            title: 'Manufacturer',
            dataIndex: 'manufacturer',
            key: 'manufacturer',
            render: (text) => text || '-',
        },
        {
            title: 'Model',
            dataIndex: 'model',
            key: 'model',
            render: (text) => text || '-',
        },
        {
            title: 'Est. Cost',
            dataIndex: 'estimatedUnitCost',
            key: 'estimatedUnitCost',
            render: (cost) => cost ? `₵${cost.toFixed(2)}` : '-',
            sorter: (a, b) => (a.estimatedUnitCost || 0) - (b.estimatedUnitCost || 0),
        },
        {
            title: 'UOM',
            dataIndex: 'defaultUnitOfMeasure',
            key: 'defaultUnitOfMeasure',
            filters: unitsOfMeasure.map(uom => ({ text: uom, value: uom })),
            onFilter: (value, record) => record.defaultUnitOfMeasure === value,
        },
        {
            title: 'Depreciable',
            dataIndex: 'isDepreciable',
            key: 'isDepreciable',
            render: (isDepreciable) => (
                <Tag color={isDepreciable ? 'blue' : 'default'}>
                    {isDepreciable ? 'Yes' : 'No'}
                </Tag>
            ),
            filters: [
                { text: 'Yes', value: true },
                { text: 'No', value: false },
            ],
            onFilter: (value, record) => record.isDepreciable === value,
        },
        {
            title: 'Useful Life',
            dataIndex: 'usefulLifeYears',
            key: 'usefulLifeYears',
            render: (years) => years ? `${years} yrs` : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item key="view" onClick={() => handleViewDetails(record, 'asset')}>
                                <EyeOutlined /> View Details
                            </Menu.Item>
                            <Menu.Item key="edit" onClick={() => handleEditAsset(record)}>
                                <EditOutlined /> Edit
                            </Menu.Item>
                            <Menu.Item key="copy">
                                <CopyOutlined /> Duplicate
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item key="viewInstances" onClick={() => {
                                setActiveTab('2');
                                handleAssetSelectForInstances(record.id);
                            }}>
                                <EyeOutlined /> View Instances ({assetInstances.filter(i => i.fixedAssetId === record.id).length})
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item key="delete" danger>
                                <Popconfirm
                                    title="Are you sure you want to delete this asset?"
                                    onConfirm={() => handleDeleteAsset(record.id)}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <DeleteOutlined /> Delete
                                </Popconfirm>
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={["click"]}
                >
                    <Button icon={<EllipsisOutlined />} size="small" />
                </Dropdown>
            ),
        },
    ];

    const instanceColumns = [
        {
            title: 'Tag Number',
            dataIndex: 'tagNumber',
            key: 'tagNumber',
            fixed: 'left',
            width: 120,
            render: (text) => <Tag color="purple">{text}</Tag>,
            sorter: (a, b) => a.tagNumber.localeCompare(b.tagNumber),
        },
        {
            title: 'Serial Number',
            dataIndex: 'serialNumber',
            key: 'serialNumber',
            render: (text) => <code>{text || '-'}</code>,
        },
        {
            title: 'Location',
            dataIndex: 'currentLocationDescription',
            key: 'currentLocationDescription',
            render: (text) => text || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colorMap = {
                    'In Service': 'green',
                    'Under Maintenance': 'orange',
                    'Disposed': 'red',
                    'In Transit': 'blue',
                    'Reserved': 'cyan',
                    'Retired': 'gray'
                };
                return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
            },
            filters: assetStatuses.map(status => ({ text: status, value: status })),
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Acquisition Date',
            dataIndex: 'acquisitionDate',
            key: 'acquisitionDate',
            render: (date) => date ? moment(date).format('DD/MM/YYYY') : '-',
            sorter: (a, b) => moment(a.acquisitionDate).valueOf() - moment(b.acquisitionDate).valueOf(),
        },
        {
            title: 'Cost',
            dataIndex: 'acquisitionCost',
            key: 'acquisitionCost',
            render: (cost) => cost ? `₵${cost.toFixed(2)}` : '-',
            sorter: (a, b) => (a.acquisitionCost || 0) - (b.acquisitionCost || 0),
        },
        {
            title: 'Supplier',
            dataIndex: 'supplierId',
            key: 'supplierId',
            render: (supplierId) => {
                const supplier = suppliers.find(s => s.id === supplierId);
                return supplier ? (
                    <Tooltip title={`${supplier.email || ''} ${supplier.phone || ''}`}>
                        <Tag color="blue">{supplier.name}</Tag>
                    </Tooltip>
                ) : supplierId ? (
                    <Tag color="default">{supplierId.substring(0, 8)}...</Tag>
                ) : '-';
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<HistoryOutlined />}
                        onClick={() => {
                            setActiveTab('3');
                            handleInstanceSelectForMovements(record.id);
                        }}
                        title="View Movements"
                    />
                    <Button 
                        size="small" 
                        icon={<AuditOutlined />}
                        onClick={() => {
                            setActiveTab('4');
                            handleInstanceSelectForAudits(record.id);
                        }}
                        title="View Audits"
                    />
                    <Dropdown
                        overlay={
                            <Menu>
                                <Menu.Item key="view" onClick={() => handleViewDetails(record, 'instance')}>
                                    <EyeOutlined /> View Details
                                </Menu.Item>
                                <Menu.Item key="edit" onClick={() => handleEditInstance(record)}>
                                    <EditOutlined /> Edit
                                </Menu.Item>
                                <Menu.Divider />
                                <Menu.Item key="delete" danger>
                                    <Popconfirm
                                        title="Are you sure you want to delete this instance?"
                                        onConfirm={() => handleDeleteInstance(record.id)}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <DeleteOutlined /> Delete
                                    </Popconfirm>
                                </Menu.Item>
                            </Menu>
                        }
                        trigger={["click"]}
                    >
                        <Button size="small" icon={<EllipsisOutlined />} />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    const movementColumns = [
        {
            title: 'Date',
            dataIndex: 'movementDate',
            key: 'movementDate',
            width: 150,
            render: (date) => date ? moment(date).format('DD/MM/YYYY HH:mm') : '-',
            sorter: (a, b) => moment(a.movementDate).valueOf() - moment(b.movementDate).valueOf(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'From',
            key: 'from',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    {record.fromWarehouseId && (
                        <Tag icon={<HomeOutlined />} color="blue">
                            {getWarehouseName(record.fromWarehouseId)}
                        </Tag>
                    )}
                    {record.fromShopId && (
                        <Tag icon={<ShopOutlined />} color="green">
                            {getShopName(record.fromShopId)}
                        </Tag>
                    )}
                    {!record.fromWarehouseId && !record.fromShopId && <span>-</span>}
                </Space>
            ),
        },
        {
            title: 'To',
            key: 'to',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    {record.toWarehouseId && (
                        <Tag icon={<HomeOutlined />} color="blue">
                            {getWarehouseName(record.toWarehouseId)}
                        </Tag>
                    )}
                    {record.toShopId && (
                        <Tag icon={<ShopOutlined />} color="green">
                            {getShopName(record.toShopId)}
                        </Tag>
                    )}
                    {!record.toWarehouseId && !record.toShopId && <span>-</span>}
                </Space>
            ),
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
            render: (text) => text || '-',
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            render: (text) => (
                <Tooltip title={text}>
                    {truncateText(text, 30)}
                </Tooltip>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item key="edit" onClick={() => handleEditMovement(record)}>
                                <EditOutlined /> Edit
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item key="delete" danger>
                                <Popconfirm
                                    title="Are you sure you want to delete this movement?"
                                    onConfirm={() => handleDeleteMovement(record.id)}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <DeleteOutlined /> Delete
                                </Popconfirm>
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={["click"]}
                >
                    <Button icon={<EllipsisOutlined />} size="small" />
                </Dropdown>
            ),
        },
    ];

    const auditColumns = [
        {
            title: 'Audit Date',
            dataIndex: 'auditDate',
            key: 'auditDate',
            width: 150,
            render: (date) => date ? moment(date).format('DD/MM/YYYY HH:mm') : '-',
            sorter: (a, b) => moment(a.auditDate).valueOf() - moment(b.auditDate).valueOf(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag icon={getAuditStatusIcon(status)} color={getAuditStatusColor(status)}>
                    {status}
                </Tag>
            ),
            filters: auditStatuses.map(status => ({ text: status, value: status })),
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            render: (text) => (
                <Tooltip title={text}>
                    {truncateText(text, 50)}
                </Tooltip>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item key="edit" onClick={() => handleEditAudit(record)}>
                                <EditOutlined /> Edit
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item key="delete" danger>
                                <Popconfirm
                                    title="Are you sure you want to delete this audit?"
                                    onConfirm={() => handleDeleteAudit(record.id)}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <DeleteOutlined /> Delete
                                </Popconfirm>
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={["click"]}
                >
                    <Button icon={<EllipsisOutlined />} size="small" />
                </Dropdown>
            ),
        },
    ];

    return (
        <div className="container-fluid mt-4">
            {/* Summary Cards */}
            <Row gutter={16} className="mb-4">
                <Col xs={24} sm={12} md={6}>
                    <Card hoverable className="summary-card">
                        <Statistic
                            title="Total Assets"
                            value={summaryStats.totalAssets}
                            prefix={<FileTextOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card hoverable className="summary-card">
                        <Statistic
                            title="Total Instances"
                            value={summaryStats.totalInstances}
                            prefix={<CopyOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card hoverable className="summary-card">
                        <Statistic
                            title="Total Value"
                            value={summaryStats.totalValue}
                            precision={2}
                            prefix="₵"
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card hoverable className="summary-card">
                        <Statistic
                            title="In Service"
                            value={summaryStats.inServiceCount}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#13c2c2' }}
                        />
                    </Card>
                </Col>
            </Row>

            <div className="card shadow-sm">
                <div className="card-header bg-white py-3">
                    <Row align="middle" justify="space-between">
                        <Col>
                            <h4 className="mb-0">
                                <span style={{ color: '#1890ff' }}>{businessName}</span> - Fixed Assets Management
                            </h4>
                        </Col>
                        <Col>
                            <Space>
                                <Button icon={<ReloadOutlined />} onClick={() => fetchFixedAssets()}>
                                    Refresh
                                </Button>
                                <Button icon={<DownloadOutlined />} onClick={() => exportToCSV(fixedAssets, 'assets')}>
                                    Export
                                </Button>
                                <Button icon={<PrinterOutlined />} onClick={printReport}>
                                    Print
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>

                <div className="card-body">
                    <Tabs 
                        activeKey={activeTab} 
                        onChange={setActiveTab}
                        type="card"
                        className="mb-4"
                    >
                        <TabPane 
                            tab={<span><FileTextOutlined />Fixed Assets ({fixedAssets.length})</span>} 
                            key="1"
                        >
                            <Space style={{ marginBottom: 16 }} wrap>
                                <Button
                                    type="primary"
                                    onClick={handleAddAsset}
                                    icon={<PlusOutlined />}
                                >
                                    Add Fixed Asset
                                </Button>
                                <Input.Search
                                    placeholder="Search by name, code, manufacturer..."
                                    value={searchText}
                                    onChange={(e) => handleAssetSearch(e.target.value)}
                                    style={{ width: 350 }}
                                    allowClear
                                />
                            </Space>

                            <Table
                                columns={assetColumns}
                                dataSource={filteredFixedAssets}
                                rowKey="id"
                                loading={loading}
                                pagination={{ 
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total) => `Total ${total} assets`
                                }}
                                bordered
                                scroll={{ x: 1500 }}
                                size="middle"
                            />
                        </TabPane>

                        <TabPane 
                            tab={<span><CopyOutlined />Asset Instances ({assetInstances.length})</span>} 
                            key="2"
                        >
                            <Card size="small" className="mb-3" style={{ background: '#f5f5f5' }}>
                                <Row gutter={16} align="middle">
                                    <Col xs={24} md={10}>
                                        <Select
                                            placeholder="Select Fixed Asset to view instances"
                                            style={{ width: '100%' }}
                                            onChange={handleAssetSelectForInstances}
                                            value={selectedAssetForInstances?.id}
                                            showSearch
                                            optionFilterProp="children"
                                            allowClear
                                        >
                                            {fixedAssets.map(asset => (
                                                <Option key={asset.id} value={asset.id}>
                                                    {asset.code} - {asset.name} ({asset.model})
                                                </Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    <Col xs={24} md={14}>
                                        <Space wrap>
                                            <Button
                                                type="primary"
                                                onClick={handleAddInstance}
                                                icon={<PlusOutlined />}
                                                disabled={!selectedAssetForInstances}
                                            >
                                                Add Instance
                                            </Button>
                                            {selectedAssetForInstances && (
                                                <Badge 
                                                    status="processing" 
                                                    text={`Showing: ${selectedAssetForInstances.name}`}
                                                    style={{ backgroundColor: '#e6f7ff', padding: '4px 12px', borderRadius: '20px' }}
                                                />
                                            )}
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            {selectedAssetForInstances ? (
                                <>
                                    <Space style={{ marginBottom: 16 }} wrap>
                                        <Input.Search
                                            placeholder="Search by serial, tag, location..."
                                            value={instanceSearchText}
                                            onChange={(e) => handleInstanceSearch(e.target.value)}
                                            style={{ width: 350 }}
                                            allowClear
                                        />
                                    </Space>

                                    <Table
                                        columns={instanceColumns}
                                        dataSource={filteredInstances}
                                        rowKey="id"
                                        loading={instancesLoading}
                                        pagination={{ 
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} instances`
                                        }}
                                        bordered
                                        scroll={{ x: 1600 }}
                                        size="middle"
                                        locale={{
                                            emptyText: 'No instances found for this fixed asset'
                                        }}
                                    />
                                </>
                            ) : (
                                <Alert
                                    message="Select a Fixed Asset"
                                    description="Please select a Fixed Asset from the dropdown above to view its instances"
                                    type="info"
                                    showIcon
                                />
                            )}
                        </TabPane>

                        <TabPane 
                            tab={<span><SwapOutlined />Asset Movements ({assetMovements.length})</span>} 
                            key="3"
                        >
                            <Card size="small" className="mb-3" style={{ background: '#f5f5f5' }}>
                                <Row gutter={16} align="middle">
                                    <Col xs={24} md={10}>
                                        <Select
                                            placeholder="Select Asset Instance to view movements"
                                            style={{ width: '100%' }}
                                            onChange={handleInstanceSelectForMovements}
                                            value={selectedInstanceForMovements?.id}
                                            showSearch
                                            optionFilterProp="children"
                                            allowClear
                                            disabled={!selectedAssetForInstances}
                                        >
                                            {assetInstances.map(instance => (
                                                <Option key={instance.id} value={instance.id}>
                                                    {instance.tagNumber} - {instance.serialNumber || 'No Serial'}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    <Col xs={24} md={14}>
                                        <Space wrap>
                                            <Button
                                                type="primary"
                                                onClick={handleAddMovement}
                                                icon={<PlusOutlined />}
                                                disabled={!selectedInstanceForMovements}
                                            >
                                                Add Movement
                                            </Button>
                                            {selectedInstanceForMovements && (
                                                <Badge 
                                                    status="processing" 
                                                    text={`Movements for: ${selectedInstanceForMovements.tagNumber}`}
                                                    style={{ backgroundColor: '#f6ffed', padding: '4px 12px', borderRadius: '20px' }}
                                                />
                                            )}
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            {selectedInstanceForMovements ? (
                                <>
                                    <Space style={{ marginBottom: 16 }} wrap>
                                        <Input.Search
                                            placeholder="Search by reason or notes..."
                                            value={movementSearchText}
                                            onChange={(e) => handleMovementSearch(e.target.value)}
                                            style={{ width: 350 }}
                                            allowClear
                                        />
                                    </Space>

                                    <Table
                                        columns={movementColumns}
                                        dataSource={filteredMovements}
                                        rowKey="id"
                                        loading={movementsLoading}
                                        pagination={{ 
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} movements`
                                        }}
                                        bordered
                                        scroll={{ x: 1300 }}
                                        size="middle"
                                        locale={{
                                            emptyText: 'No movements found for this instance'
                                        }}
                                    />
                                </>
                            ) : (
                                <Alert
                                    message="Select an Asset Instance"
                                    description={
                                        !selectedAssetForInstances 
                                            ? "Please select a Fixed Asset and then an Instance to view its movement history"
                                            : "Please select an Asset Instance from the dropdown above to view its movements"
                                    }
                                    type="info"
                                    showIcon
                                />
                            )}
                        </TabPane>

                        <TabPane 
                            tab={<span><AuditOutlined />Asset Audits ({assetAudits.length})</span>} 
                            key="4"
                        >
                            <Card size="small" className="mb-3" style={{ background: '#f5f5f5' }}>
                                <Row gutter={16} align="middle">
                                    <Col xs={24} md={10}>
                                        <Select
                                            placeholder="Select Asset Instance to view audits"
                                            style={{ width: '100%' }}
                                            onChange={handleInstanceSelectForAudits}
                                            value={selectedInstanceForAudits?.id}
                                            showSearch
                                            optionFilterProp="children"
                                            allowClear
                                            disabled={!selectedAssetForInstances}
                                        >
                                            {assetInstances.map(instance => (
                                                <Option key={instance.id} value={instance.id}>
                                                    {instance.tagNumber} - {instance.serialNumber || 'No Serial'}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    <Col xs={24} md={14}>
                                        <Space wrap>
                                            <Button
                                                type="primary"
                                                onClick={handleAddAudit}
                                                icon={<PlusOutlined />}
                                                disabled={!selectedInstanceForAudits}
                                            >
                                                Add Audit
                                            </Button>
                                            {selectedInstanceForAudits && (
                                                <Badge 
                                                    status="processing" 
                                                    text={`Audits for: ${selectedInstanceForAudits.tagNumber}`}
                                                    style={{ backgroundColor: '#fff7e6', padding: '4px 12px', borderRadius: '20px' }}
                                                />
                                            )}
                                            <Button 
                                                icon={<FilterOutlined />}
                                                onClick={() => setIsFilterModalVisible(true)}
                                                disabled={assetAudits.length === 0}
                                            >
                                                Filter
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            {selectedInstanceForAudits ? (
                                <>
                                    <Space style={{ marginBottom: 16 }} wrap>
                                        <Input.Search
                                            placeholder="Search by status or notes..."
                                            value={auditSearchText}
                                            onChange={(e) => handleAuditSearch(e.target.value)}
                                            style={{ width: 350 }}
                                            allowClear
                                        />
                                    </Space>

                                    <Table
                                        columns={auditColumns}
                                        dataSource={filteredAudits}
                                        rowKey="id"
                                        loading={auditsLoading}
                                        pagination={{ 
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} audits`
                                        }}
                                        bordered
                                        scroll={{ x: 1000 }}
                                        size="middle"
                                        locale={{
                                            emptyText: 'No audits found for this instance'
                                        }}
                                    />
                                </>
                            ) : (
                                <Alert
                                    message="Select an Asset Instance"
                                    description={
                                        !selectedAssetForInstances 
                                            ? "Please select a Fixed Asset and then an Instance to view its audit history"
                                            : "Please select an Asset Instance from the dropdown above to view its audits"
                                    }
                                    type="info"
                                    showIcon
                                />
                            )}
                        </TabPane>
                    </Tabs>
                </div>
            </div>

            {/* Fixed Asset Modal */}
            <Modal
                title={
                    <span>
                        {selectedAsset ? <EditOutlined /> : <PlusOutlined />}
                        {selectedAsset ? " Edit Fixed Asset" : " Add New Fixed Asset"}
                    </span>
                }
                open={isAssetModalVisible}
                onOk={handleAssetModalOk}
                onCancel={() => {
                    setIsAssetModalVisible(false);
                    assetForm.resetFields();
                    setSelectedAsset(null);
                }}
                okText={selectedAsset ? "Update" : "Create"}
                cancelText="Cancel"
                confirmLoading={loading}
                width={700}
                className="asset-modal"
            >
                <Form form={assetForm} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Asset Code"
                                name="code"
                                rules={[{ required: true, message: 'Please enter asset code!' }]}
                            >
                                <Input placeholder="e.g., AST-001" prefix={<FileTextOutlined />} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Asset Name"
                                name="name"
                                rules={[{ required: true, message: 'Please enter asset name!' }]}
                            >
                                <Input placeholder="Enter asset name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Description"
                        name="description"
                        rules={[{ required: true, message: 'Please enter description!' }]}
                    >
                        <TextArea placeholder="Enter detailed description" rows={3} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Manufacturer"
                                name="manufacturer"
                            >
                                <Input placeholder="Enter manufacturer name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Model"
                                name="model"
                                rules={[{ required: true, message: 'Please enter model!' }]}
                            >
                                <Input placeholder="Enter model" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Estimated Unit Cost"
                                name="estimatedUnitCost"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    precision={2}
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                    formatter={value => `₵ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/₵\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Default Unit of Measure"
                                name="defaultUnitOfMeasure"
                                rules={[{ required: true, message: 'Please select UOM!' }]}
                            >
                                <Select placeholder="Select UOM">
                                    {unitsOfMeasure.map(uom => (
                                        <Option key={uom} value={uom}>{uom}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="isDepreciable"
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="Depreciable" unCheckedChildren="Non-depreciable" />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.isDepreciable !== currentValues.isDepreciable}
                    >
                        {({ getFieldValue }) => 
                            getFieldValue('isDepreciable') && (
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Useful Life (Years)"
                                            name="usefulLifeYears"
                                            rules={[{ required: true, message: 'Please enter useful life!' }]}
                                        >
                                            <InputNumber min={1} max={50} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Depreciation Method"
                                            name="depreciationMethod"
                                            rules={[{ required: true, message: 'Please select method!' }]}
                                        >
                                            <Select placeholder="Select method">
                                                {depreciationMethods.map(method => (
                                                    <Option key={method} value={method}>{method}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )
                        }
                    </Form.Item>
                </Form>
            </Modal>

            {/* Asset Instance Modal */}
            <Modal
                title={
                    <span>
                        {selectedInstance ? <EditOutlined /> : <PlusOutlined />}
                        {selectedInstance ? " Edit Asset Instance" : " Add New Asset Instance"}
                    </span>
                }
                open={isInstanceModalVisible}
                onOk={handleInstanceModalOk}
                onCancel={() => {
                    setIsInstanceModalVisible(false);
                    instanceForm.resetFields();
                    setSelectedInstance(null);
                }}
                okText={selectedInstance ? "Update" : "Create"}
                cancelText="Cancel"
                confirmLoading={loading}
                width={700}
                className="asset-modal"
            >
                <Form form={instanceForm} layout="vertical">
                    <Form.Item
                        label="Fixed Asset"
                        name="fixedAssetId"
                        rules={[{ required: true, message: 'Please select fixed asset!' }]}
                    >
                        <Select 
                            placeholder="Select fixed asset" 
                            showSearch 
                            optionFilterProp="children"
                            disabled={!!selectedAssetForInstances}
                        >
                            {fixedAssets.map(asset => (
                                <Option key={asset.id} value={asset.id}>
                                    {asset.code} - {asset.name} ({asset.model})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Serial Number"
                                name="serialNumber"
                            >
                                <Input placeholder="Enter serial number" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Tag Number"
                                name="tagNumber"
                                rules={[{ required: true, message: 'Please enter tag number!' }]}
                            >
                                <Input placeholder="Enter tag number" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Status"
                                name="status"
                                rules={[{ required: true, message: 'Please select status!' }]}
                            >
                                <Select placeholder="Select status">
                                    {assetStatuses.map(status => (
                                        <Option key={status} value={status}>{status}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Location Description"
                                name="currentLocationDescription"
                            >
                                <Input placeholder="e.g., Warehouse A, Shelf 3" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Acquisition Date"
                                name="acquisitionDate"
                                rules={[{ required: true, message: 'Please select date!' }]}
                            >
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Acquisition Cost"
                                name="acquisitionCost"
                                rules={[{ required: true, message: 'Please enter cost!' }]}
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    precision={2}
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                    formatter={value => `₵ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/₵\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Supplier"
                                name="supplierId"
                            >
                                <Select 
                                    placeholder="Select supplier" 
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {suppliers.map(supplier => (
                                        <Option key={supplier.id} value={supplier.id}>
                                            {supplier.name} {supplier.code ? `(${supplier.code})` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Invoice Number"
                                name="invoiceNumber"
                            >
                                <Input placeholder="Enter invoice number" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Purchase Order Number"
                        name="purchaseOrderNumber"
                    >
                        <Input placeholder="Enter PO number" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Asset Movement Modal */}
            <Modal
                title={
                    <span>
                        {selectedMovement ? <EditOutlined /> : <PlusOutlined />}
                        {selectedMovement ? " Edit Asset Movement" : " Add New Asset Movement"}
                    </span>
                }
                open={isMovementModalVisible}
                onOk={handleMovementModalOk}
                onCancel={() => {
                    setIsMovementModalVisible(false);
                    movementForm.resetFields();
                    setSelectedMovement(null);
                }}
                okText={selectedMovement ? "Update" : "Create"}
                cancelText="Cancel"
                confirmLoading={loading}
                width={700}
                className="asset-modal"
            >
                <Form form={movementForm} layout="vertical">
                    <Form.Item
                        label="Asset Instance"
                        name="fixedAssetInstanceId"
                        rules={[{ required: true, message: 'Asset instance is required!' }]}
                    >
                        <Select 
                            placeholder="Select asset instance" 
                            disabled={!!selectedInstanceForMovements}
                            showSearch
                            optionFilterProp="children"
                        >
                            {assetInstances.map(instance => (
                                <Option key={instance.id} value={instance.id}>
                                    {instance.tagNumber} - {instance.serialNumber || 'No Serial'}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Movement Date"
                        name="movementDate"
                        rules={[{ required: true, message: 'Please select movement date!' }]}
                    >
                        <DatePicker 
                            style={{ width: '100%' }} 
                            format="YYYY-MM-DD HH:mm"
                            showTime={{ format: 'HH:mm' }}
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="From Warehouse"
                                name="fromWarehouseId"
                            >
                                <Select 
                                    placeholder="Select source warehouse" 
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {warehouses.map(warehouse => (
                                        <Option key={warehouse.id} value={warehouse.id}>
                                            {warehouse.name} {warehouse.code ? `(${warehouse.code})` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="To Warehouse"
                                name="toWarehouseId"
                            >
                                <Select 
                                    placeholder="Select destination warehouse" 
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {warehouses.map(warehouse => (
                                        <Option key={warehouse.id} value={warehouse.id}>
                                            {warehouse.name} {warehouse.code ? `(${warehouse.code})` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="From Shop"
                                name="fromShopId"
                            >
                                <Select 
                                    placeholder="Select source shop" 
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {shops.map(shop => (
                                        <Option key={shop.id} value={shop.id}>
                                            {shop.name} {shop.code ? `(${shop.code})` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="To Shop"
                                name="toShopId"
                            >
                                <Select 
                                    placeholder="Select destination shop" 
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {shops.map(shop => (
                                        <Option key={shop.id} value={shop.id}>
                                            {shop.name} {shop.code ? `(${shop.code})` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Reason"
                        name="reason"
                        rules={[{ required: true, message: 'Please enter reason!' }]}
                    >
                        <Select placeholder="Select reason">
                            {movementReasons.map(reason => (
                                <Option key={reason} value={reason}>{reason}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Notes"
                        name="notes"
                    >
                        <TextArea rows={3} placeholder="Enter additional notes" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Asset Audit Modal */}
            <Modal
                title={
                    <span>
                        {selectedAudit ? <EditOutlined /> : <PlusOutlined />}
                        {selectedAudit ? " Edit Asset Audit" : " Add New Asset Audit"}
                    </span>
                }
                open={isAuditModalVisible}
                onOk={handleAuditModalOk}
                onCancel={() => {
                    setIsAuditModalVisible(false);
                    auditForm.resetFields();
                    setSelectedAudit(null);
                }}
                okText={selectedAudit ? "Update" : "Create"}
                cancelText="Cancel"
                confirmLoading={loading}
                width={600}
                className="asset-modal"
            >
                <Form form={auditForm} layout="vertical">
                    <Form.Item
                        label="Asset Instance"
                        name="fixedAssetInstanceId"
                        rules={[{ required: true, message: 'Asset instance is required!' }]}
                    >
                        <Select 
                            placeholder="Select asset instance" 
                            disabled={!!selectedInstanceForAudits}
                            showSearch
                            optionFilterProp="children"
                        >
                            {assetInstances.map(instance => (
                                <Option key={instance.id} value={instance.id}>
                                    {instance.tagNumber} - {instance.serialNumber || 'No Serial'}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Audit Date"
                        name="auditDate"
                        rules={[{ required: true, message: 'Please select audit date!' }]}
                    >
                        <DatePicker 
                            style={{ width: '100%' }} 
                            format="YYYY-MM-DD HH:mm"
                            showTime={{ format: 'HH:mm' }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Status"
                        name="status"
                        rules={[{ required: true, message: 'Please select status!' }]}
                    >
                        <Select placeholder="Select status">
                            {auditStatuses.map(status => (
                                <Option key={status} value={status}>{status}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Notes"
                        name="notes"
                    >
                        <TextArea rows={4} placeholder="Enter audit findings, observations, or notes" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Filter Modal */}
            <Modal
                title={<span><FilterOutlined /> Filter Audits</span>}
                open={isFilterModalVisible}
                onOk={applyFilters}
                onCancel={resetFilters}
                okText="Apply Filters"
                cancelText="Reset"
                width={500}
            >
                <Form layout="vertical">
                    <Form.Item label="Status">
                        <Select
                            placeholder="Filter by status"
                            value={statusFilter}
                            onChange={setStatusFilter}
                            allowClear
                        >
                            {auditStatuses.map(status => (
                                <Option key={status} value={status}>{status}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Date Range">
                        <RangePicker 
                            style={{ width: '100%' }}
                            value={dateRange}
                            onChange={setDateRange}
                            format="YYYY-MM-DD"
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* View Details Modal */}
            <Modal
                title={
                    <span>
                        {viewType === 'asset' && <FileTextOutlined />}
                        {viewType === 'instance' && <CopyOutlined />}
                        {viewType === 'movement' && <SwapOutlined />}
                        {viewType === 'audit' && <AuditOutlined />}
                        {' '}
                        {viewType === 'asset' ? 'Fixed Asset Details' : 
                         viewType === 'instance' ? 'Asset Instance Details' : 
                         viewType === 'movement' ? 'Movement Details' : 
                         'Audit Details'}
                    </span>
                }
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={600}
                className="view-modal"
            >
                {viewingItem && viewType === 'asset' && (
                    <Descriptions bordered column={1} size="middle">
                        <Descriptions.Item label="Code"><Tag color="blue">{viewingItem.code}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Name"><strong>{viewingItem.name}</strong></Descriptions.Item>
                        <Descriptions.Item label="Description">{viewingItem.description}</Descriptions.Item>
                        <Descriptions.Item label="Manufacturer">{viewingItem.manufacturer || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Model">{viewingItem.model || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Estimated Unit Cost">
                            {viewingItem.estimatedUnitCost ? `₵${viewingItem.estimatedUnitCost.toFixed(2)}` : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Unit of Measure">{viewingItem.defaultUnitOfMeasure}</Descriptions.Item>
                        <Descriptions.Item label="Depreciable">
                            <Tag color={viewingItem.isDepreciable ? 'blue' : 'default'}>
                                {viewingItem.isDepreciable ? 'Yes' : 'No'}
                            </Tag>
                        </Descriptions.Item>
                        {viewingItem.isDepreciable && (
                            <>
                                <Descriptions.Item label="Useful Life">{viewingItem.usefulLifeYears} years</Descriptions.Item>
                                <Descriptions.Item label="Depreciation Method">{viewingItem.depreciationMethod}</Descriptions.Item>
                            </>
                        )}
                    </Descriptions>
                )}

                {viewingItem && viewType === 'instance' && (
                    <Descriptions bordered column={1} size="middle">
                        <Descriptions.Item label="Asset">
                            {fixedAssets.find(a => a.id === viewingItem.fixedAssetId)?.name || viewingItem.fixedAssetId}
                        </Descriptions.Item>
                        <Descriptions.Item label="Serial Number"><code>{viewingItem.serialNumber || '-'}</code></Descriptions.Item>
                        <Descriptions.Item label="Tag Number"><Tag color="purple">{viewingItem.tagNumber}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color={
                                viewingItem.status === 'In Service' ? 'green' :
                                viewingItem.status === 'Under Maintenance' ? 'orange' :
                                viewingItem.status === 'Disposed' ? 'red' : 'default'
                            }>
                                {viewingItem.status}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Location">{viewingItem.currentLocationDescription || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Acquisition Date">
                            {viewingItem.acquisitionDate ? moment(viewingItem.acquisitionDate).format('DD/MM/YYYY') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Acquisition Cost">
                            {viewingItem.acquisitionCost ? `₵${viewingItem.acquisitionCost.toFixed(2)}` : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Supplier">
                            {getSupplierName(viewingItem.supplierId)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Invoice Number">{viewingItem.invoiceNumber || '-'}</Descriptions.Item>
                        <Descriptions.Item label="PO Number">{viewingItem.purchaseOrderNumber || '-'}</Descriptions.Item>
                    </Descriptions>
                )}

                {viewingItem && viewType === 'movement' && (
                    <Descriptions bordered column={1} size="middle">
                        <Descriptions.Item label="Movement Date">
                            {moment(viewingItem.movementDate).format('DD/MM/YYYY HH:mm')}
                        </Descriptions.Item>
                        <Descriptions.Item label="From Warehouse">
                            {getWarehouseName(viewingItem.fromWarehouseId) || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="To Warehouse">
                            {getWarehouseName(viewingItem.toWarehouseId) || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="From Shop">
                            {getShopName(viewingItem.fromShopId) || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="To Shop">
                            {getShopName(viewingItem.toShopId) || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Reason">{viewingItem.reason}</Descriptions.Item>
                        <Descriptions.Item label="Notes">{viewingItem.notes || '-'}</Descriptions.Item>
                    </Descriptions>
                )}

                {viewingItem && viewType === 'audit' && (
                    <Descriptions bordered column={1} size="middle">
                        <Descriptions.Item label="Audit Date">
                            {moment(viewingItem.auditDate).format('DD/MM/YYYY HH:mm')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag icon={getAuditStatusIcon(viewingItem.status)} color={getAuditStatusColor(viewingItem.status)}>
                                {viewingItem.status}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Notes">{viewingItem.notes || '-'}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            <style jsx>{`
                .summary-card {
                    border-radius: 8px;
                    transition: all 0.3s;
                }
                .summary-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                .asset-modal .ant-modal-body {
                    max-height: 70vh;
                    overflow-y: auto;
                }
                .view-modal .ant-descriptions-item-label {
                    width: 150px;
                    background-color: #fafafa;
                }
            `}</style>
        </div>
    );
};

export default AssetsPage;